const img = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1400&q=80`;

// Explainer films: short illustrative clips (generated via fal.ai) with the article's key points.
// Set youtubeId to switch to a hosted clinician film when one is published.
const explainer = (title, slug, points) => ({ title, youtubeId: null, src: `/videos/${slug}.mp4`, points });

export const CLINICAL_ARTICLES = [
  {
    slug: "adam-questionnaire",
    label: "SYMPTOM CHECK",
    title: "The ADAM questionnaire: a 10-question low testosterone screen",
    lede: "The Androgen Deficiency in Aging Males (ADAM) questionnaire is the most widely used symptom screen for low testosterone. Take it here, then understand exactly what a 'positive' result does — and does not — mean.",
    reading: "4 min read + 2 min questionnaire",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1582298538104-fe2e74c27f59"),
    imageAlt: "Group of friends laughing together over coffee",
    featured: true,
    widget: "adam",
    answer:
      "The ADAM questionnaire asks ten yes/no questions about libido, energy, strength, mood, erections and daily performance. It is 'positive' if you answer yes to question 1 (libido) or question 7 (erections), or yes to any three other questions. A positive screen is a reason to arrange a morning testosterone blood test — not a diagnosis. The questionnaire is deliberately sensitive, which means many men with normal testosterone also screen positive.",
    body: [
      {
        h2: "What the ADAM questionnaire is for",
        paras: [
          "ADAM was developed by Morley and colleagues at Saint Louis University in 2000 as a quick way to identify men whose symptoms warranted a blood test. It was never designed to diagnose testosterone deficiency on its own, and the authors said so. Its job is to open the door to testing, not to decide what is behind it.",
          "That design choice matters. The questionnaire casts a wide net: it picks up most men who do have low testosterone, at the cost of also flagging many whose symptoms have other causes — stress, poor sleep, depression, excess weight, relationship difficulties or simply a heavy season of life.",
        ],
      },
      {
        h2: "How the scoring works",
        paras: [
          "Two questions carry extra weight because they relate most directly to androgen-dependent function: a fall in libido (question 1) and weaker erections (question 7). A 'yes' to either is a positive screen by itself. Otherwise, three or more 'yes' answers across the remaining eight questions is also positive.",
        ],
        bullets: [
          "Positive: yes to Q1 (libido) or Q7 (erections), regardless of other answers.",
          "Positive: yes to any three of the other eight questions.",
          "Negative: everything else — which does not rule out deficiency if symptoms are significant.",
        ],
      },
      {
        h2: "What to do with a positive result",
        paras: [
          "Arrange a properly timed blood test: total testosterone collected in the morning, ideally fasting, with SHBG and free testosterone where indicated. If the first result is low or borderline, it should be repeated before anyone talks about treatment. A clinician then interprets the numbers alongside your history, examination findings and other possible causes.",
          "If the result is negative but your symptoms are persistent and affecting your life, you still deserve an assessment — ADAM misses some men, and the symptoms themselves warrant attention whatever the cause.",
        ],
      },
      {
        h2: "The honest limitations",
        paras: [
          "Because almost every ADAM item overlaps with ordinary tiredness, low mood and ageing, the questionnaire has a high false-positive rate. Clinics that use a positive ADAM score as the end of the conversation rather than the start of one are using it wrongly. At Androgen Health the result is recorded as one data point in a structured assessment — alongside two morning blood tests, other hormones and safety markers — and never as grounds for a prescription.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a positive ADAM result the same as having low testosterone?",
        a: "No. A positive result means a blood test is sensible. Many men with a positive ADAM screen have normal testosterone and symptoms caused by something else.",
      },
      {
        q: "Does Androgen Health use the ADAM questionnaire?",
        a: "Yes, as part of a structured history — never on its own. A diagnosis requires repeated morning blood tests, interpretation of other hormones and an assessment of other causes.",
      },
      {
        q: "Are there other questionnaires?",
        a: "Yes. The Aging Males' Symptoms (AMS) scale and the qADAM are also used. They share the same limitation: useful for screening and tracking, not for diagnosis.",
      },
    ],
    related: ["low-testosterone-symptoms", "normal-testosterone-levels", "causes-of-low-testosterone"],
  },

  {
    slug: "trt-side-effects-and-risks",
    label: "SAFETY",
    title: "TRT side effects and risks: what the evidence actually says",
    lede: "Testosterone therapy is well understood, but not risk-free. Here is a straight account of the common effects, the serious ones, and how structured monitoring is designed to catch them early.",
    reading: "7 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1765718482370-1205a87400c9"),
    imageAlt: "Man looking thoughtfully into the distance",
    featured: true,
    video: explainer("The risks we monitor for, and why", "trt-side-effects-and-risks", ["Common effects: acne, fluid retention, raised haematocrit, reduced sperm production.", "Haematocrit is the number that most often changes a dose.", "PSA is checked because testosterone can accelerate an existing prostate cancer.", "TRAVERSE (2023): no rise in major cardiac events in appropriately selected men.", "Screening and scheduled monitoring are the treatment, not an add-on."]),
    answer:
      "The most common effects of TRT are acne or oily skin, fluid retention, increased red blood cell count (raised haematocrit), reduced sperm production and testicular shrinkage, and changes in mood or sleep. Less common but important risks include worsening of sleep apnoea, breast tenderness, and the need for prostate monitoring. Large recent trials have been reassuring about cardiovascular risk in appropriately selected men, but this is why eligibility and monitoring matter. TRT is not suitable for everyone.",
    body: [
      {
        h2: "Common and usually manageable",
        paras: [
          "Most side effects are predictable consequences of raising testosterone and can be managed by adjusting dose, formulation or timing. They are discussed before treatment, not discovered after it.",
        ],
        bullets: [
          "Skin changes — acne and oiliness, more often with higher peaks after injections.",
          "Fluid retention and mild weight gain in the first months.",
          "Raised haematocrit — the blood becoming 'thicker' — which is monitored on a schedule.",
          "Reduced sperm production and smaller testes, because the body's own signal is switched off.",
          "Mood or sleep changes, particularly if levels swing between doses.",
          "Site reactions: skin irritation with gels, soreness after injections.",
        ],
      },
      {
        h2: "The risks that drive the monitoring schedule",
        paras: [
          "Haematocrit is the single most important number after your testosterone level. If it rises too far, the risk of clotting increases and the dose must be reduced, paused or the formulation changed. Prostate-specific antigen (PSA) is checked because testosterone can accelerate an existing prostate cancer — not because TRT is believed to cause one. Men with untreated obstructive sleep apnoea can find it worsens. Rarely, breast tissue can become tender or enlarge as some testosterone converts to oestrogen.",
        ],
      },
      {
        h2: "Heart health: what changed in the evidence",
        paras: [
          "For a decade the question of whether TRT raised cardiovascular risk was genuinely unsettled, and regulators in the UK and US asked for caution. The TRAVERSE trial, published in 2023, followed more than five thousand middle-aged and older men with confirmed hypogonadism and existing or high cardiovascular risk, and did not find an increase in major cardiovascular events with testosterone compared with placebo over the study period. It did observe more atrial fibrillation, pulmonary embolism and acute kidney injury in the treatment group — which is exactly why screening and monitoring exist. It is reassuring evidence for appropriately selected men; it is not a licence to treat anyone.",
        ],
      },
      {
        h2: "Who should not start, or should pause",
        bullets: [
          "Men planning to conceive now or soon, without fertility counselling and a plan.",
          "Known or suspected prostate or male breast cancer.",
          "A significantly raised haematocrit before treatment.",
          "Severe untreated sleep apnoea or uncontrolled heart failure.",
          "A heart attack or stroke within the last six months.",
        ],
      },
      {
        h2: "How we reduce risk in practice",
        paras: [
          "Assessment first — including confirming the diagnosis with repeated morning tests. Baseline haematocrit, PSA and other safety markers before any prescription. Reviews at defined intervals in the first year, then ongoing. Documented escalation if a result is abnormal, including pausing treatment or referring onward. These are not add-ons; they are the treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does TRT cause prostate cancer?",
        a: "Current evidence does not show that TRT causes prostate cancer. It may accelerate an existing, undiagnosed cancer, which is why PSA and prostate assessment are part of eligibility and monitoring.",
      },
      {
        q: "Will TRT make me infertile?",
        a: "TRT suppresses sperm production in most men while they are on it, and recovery after stopping can take months and is not guaranteed. Fertility intentions are discussed before any treatment decision.",
      },
      {
        q: "What symptoms should make me seek urgent help?",
        a: "Chest pain, breathlessness, one-sided weakness or slurred speech, a swollen painful calf, or coughing up blood need urgent care — 999 in an emergency, 111 otherwise. Do not wait for a routine review.",
      },
    ],
    related: ["haematocrit-and-blood-monitoring-on-trt", "prostate-health-psa-and-trt", "trt-and-heart-health", "trt-and-fertility"],
  },

  {
    slug: "trt-and-fertility",
    label: "FERTILITY",
    title: "TRT and fertility: what every man should know before starting",
    lede: "Testosterone therapy switches off the signal that drives sperm production. For men who may want children, that changes the whole conversation — which is why we have it first.",
    reading: "6 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1564989951309-d4cfecbf1990"),
    imageAlt: "Father and son walking together on a hillside path",
    featured: true,
    video: explainer("Fertility counselling before TRT", "trt-and-fertility", ["TRT switches off LH and FSH — the signals that drive sperm production.", "Most men become sub-fertile on treatment; recovery can take months to two years.", "Options: delay, treat the cause, fertility-preserving alternatives, or freeze sperm first.", "TRT is not a reliable contraceptive.", "We have this conversation before any prescription."]),
    answer:
      "Taking testosterone suppresses the pituitary hormones LH and FSH that tell the testes to make sperm. Most men on TRT become sub-fertile or infertile while on it; recovery after stopping usually takes several months to two years and is not guaranteed. If you may want children, options include delaying TRT, treating the cause, using fertility-preserving alternatives such as clomifene or hCG under specialist care, or freezing sperm beforehand. This is discussed before any treatment decision at Androgen Health.",
    body: [
      {
        h2: "Why TRT affects fertility",
        paras: [
          "Sperm production depends on very high testosterone concentrations inside the testes, driven by LH and FSH from the pituitary. When testosterone arrives from outside the body, the pituitary senses plenty and switches those signals off. Blood levels look healthy; testicular levels fall and sperm counts drop — often to very low or zero. The testes also shrink, which men notice and clinics should warn about.",
        ],
      },
      {
        h2: "Is it reversible?",
        paras: [
          "Usually, but not reliably and not quickly. After stopping testosterone, sperm production generally recovers over months, with some men taking two years or more. Recovery is less certain with longer duration of treatment, older age and pre-existing fertility problems. Anyone told that fertility 'just comes back' is being given a hopeful average, not a promise.",
        ],
      },
      {
        h2: "The options if you may want children",
        bullets: [
          "Delay TRT and treat modifiable causes first — weight, sleep, alcohol, medication review.",
          "Fertility-preserving alternatives: medicines such as clomifene (which raises your own LH and FSH) or hCG, prescribed and monitored by a specialist. These are not licensed for this use in the UK and require informed, documented consent.",
          "Sperm cryopreservation before starting — straightforward, and a sensible insurance policy.",
          "A semen analysis before treatment, so you know your baseline rather than guessing.",
        ],
      },
      {
        h2: "What good care looks like",
        paras: [
          "A fertility conversation before the first prescription, documented. A clear statement that TRT is a contraceptive-like effect but is not a reliable contraceptive. Referral to fertility services where appropriate. And a willingness to recommend against TRT — or in favour of an alternative — when a man's family plans make it the wrong tool.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I use TRT as contraception?",
        a: "No. Although TRT lowers sperm counts in most men, it is not reliable enough to be used as contraception, and some men remain fertile on it.",
      },
      {
        q: "I'm already on TRT and want a child — what now?",
        a: "Speak to your clinician before stopping anything. Options include a supervised pause, switching to fertility-preserving treatment, or adding medicines that restart sperm production. Semen analysis guides the plan.",
      },
      {
        q: "Does Androgen Health offer sperm freezing?",
        a: "Not directly. We discuss it before treatment and signpost to licensed UK fertility clinics so you can arrange it before starting.",
      },
    ],
    related: ["trt-side-effects-and-risks", "causes-of-low-testosterone", "stopping-trt"],
  },

  {
    slug: "haematocrit-and-blood-monitoring-on-trt",
    label: "MONITORING",
    title: "Haematocrit and blood monitoring on TRT",
    lede: "The most important number after your testosterone level is one most men have never heard of. Here is why haematocrit matters, what the schedule looks like, and what happens when a result is high.",
    reading: "5 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1526506118085-60ce8714f8c5"),
    imageAlt: "Man doing pull-ups in a gym, black and white",
    video: explainer("Reading your monitoring bloods", "haematocrit-and-blood-monitoring-on-trt", ["Haematocrit: the share of blood made of red cells — testosterone pushes it up.", "Checked before treatment, at 3 months, 6–12 months, then at least yearly.", "Above about 0.54 we act: lower dose, change formulation, pause or venesect.", "PSA, liver, kidney and lipids are reviewed alongside.", "Monitoring is what makes long-term treatment safe."]),
    answer:
      "Testosterone stimulates red blood cell production. In some men on TRT, haematocrit — the proportion of blood made up of red cells — rises above the normal range, making blood thicker and raising clotting risk. UK and European guidance recommends checking haematocrit before treatment, at around three and six to twelve months, then at least annually, and acting when it climbs above roughly 0.54 by reducing the dose, changing formulation, pausing treatment or arranging venesection.",
    body: [
      {
        h2: "What haematocrit is",
        paras: [
          "Haematocrit is the fraction of your blood volume occupied by red blood cells, reported either as a percentage or a decimal (0.45 and 45% are the same thing). Normal adult male values sit roughly between 0.40 and 0.52 depending on the laboratory. Testosterone increases the signal for red cell production, so a modest rise is expected; the question is how far.",
        ],
      },
      {
        h2: "Why it matters",
        paras: [
          "Thicker blood flows less easily and is more prone to clotting. Secondary erythrocytosis — a raised red cell count driven by testosterone — is the most common reason a dose has to be reduced or treatment paused. It is more likely with injectable formulations that produce high peaks, in smokers, in men with sleep apnoea, and in men whose haematocrit was already at the upper end before starting.",
        ],
      },
      {
        h2: "The monitoring schedule",
        bullets: [
          "Before starting: full blood count including haematocrit, PSA, liver and kidney function, lipids, and baseline testosterone (repeated).",
          "Around 3 months: testosterone level, haematocrit, PSA, symptom review and side effects.",
          "6–12 months: repeat the above and confirm the dose is right.",
          "Then at least annually, for as long as treatment continues — more often if a result was borderline.",
        ],
      },
      {
        h2: "What happens when haematocrit is high",
        paras: [
          "Guidelines generally advise action when haematocrit exceeds about 0.54. Options, in roughly escalating order: reduce the dose or lengthen the interval; switch from a high-peak injection to a steadier formulation; address contributing factors such as smoking, dehydration or sleep apnoea; pause treatment until the value falls; or arrange therapeutic venesection (removing a unit of blood). Which option is right depends on how high the value is, how fast it rose, and your other risks — a clinical decision, not a rule.",
        ],
      },
      {
        h2: "The other numbers we watch",
        paras: [
          "PSA and prostate symptoms; liver and kidney function; lipids and blood pressure; oestradiol where symptoms suggest it; and, for men on gels, the testosterone level itself, since absorption varies between individuals. Monitoring is not paperwork. It is the mechanism that makes long-term treatment safe.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I donate blood to lower my haematocrit?",
        a: "Some men on TRT give blood regularly, but UK blood services have eligibility rules and it should be discussed with your clinician rather than used as a self-managed workaround. Therapeutic venesection, where needed, is arranged clinically.",
      },
      {
        q: "Will my haematocrit keep rising forever?",
        a: "No. It usually rises over the first six to twelve months and then plateaus. That is why the early checks are more frequent.",
      },
    ],
    related: ["trt-side-effects-and-risks", "trt-monitoring", "testosterone-gel-vs-injections"],
  },

  {
    slug: "prostate-health-psa-and-trt",
    label: "SAFETY",
    title: "Prostate health, PSA and testosterone therapy",
    lede: "The old fear was that testosterone 'feeds' prostate cancer. The modern evidence is more nuanced — and the monitoring that follows from it is straightforward.",
    reading: "5 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1758600431229-191932ccee81"),
    imageAlt: "Older man in a plaid shirt",
    answer:
      "Current evidence does not show that testosterone therapy causes prostate cancer in men with normal prostates, and TRT may modestly improve urinary symptoms in some men. However, testosterone can accelerate the growth of an existing, undiagnosed cancer, so PSA is measured before treatment and monitored during it, and men with known or suspected prostate cancer should not start TRT without specialist oncology input. A small PSA rise in the first months is expected; a rapid or sustained rise triggers urology referral.",
    body: [
      {
        h2: "Where the fear came from",
        paras: [
          "In the 1940s, Huggins showed that lowering testosterone shrank advanced prostate cancer — work that won a Nobel Prize and shaped medical thinking for decades. The logical leap that raising testosterone must therefore cause or fuel cancer was understandable, but it has not been borne out by large studies of men on TRT, and the 'saturation model' suggests prostate tissue stops responding to additional testosterone above fairly low concentrations.",
        ],
      },
      {
        h2: "What the evidence shows now",
        paras: [
          "Observational studies and the available randomised data have not found an increased incidence of prostate cancer in men treated with testosterone compared with those not treated, although the trials were not designed primarily to answer that question and longer follow-up continues. The caution that remains is about existing cancer: a tumour already present may grow faster when testosterone is restored, so screening before treatment is essential.",
        ],
      },
      {
        h2: "PSA: what we measure and why",
        bullets: [
          "Baseline PSA before treatment — the number against which all later results are read.",
          "A PSA at 3–6 months, then annually, in line with UK and European guidance.",
          "An expected small rise after starting, as the prostate responds to restored testosterone.",
          "Referral triggers: a confirmed PSA above the age-appropriate threshold, a rise of more than about 1.4 ng/mL in 12 months, or new symptoms or examination findings.",
        ],
      },
      {
        h2: "Urinary symptoms and benign enlargement",
        paras: [
          "Benign prostatic enlargement is common with age and causes urinary frequency, hesitancy and night-time waking. TRT does not appear to worsen these symptoms in most men, and may improve them in some, but severe lower urinary tract symptoms are assessed before starting so that a change can be attributed correctly.",
        ],
      },
      {
        h2: "If you have had prostate cancer",
        paras: [
          "Testosterone therapy after treated prostate cancer is an area of active research and specialist practice. It is not something an online clinic should start without your urologist or oncologist being involved. At Androgen Health a history of prostate cancer leads to a shared decision with your specialist team, or to advising against treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will I need a rectal examination?",
        a: "A digital rectal examination is part of good prostate assessment, particularly in older men or where PSA is raised. Where it is indicated, we arrange it through an appropriate in-person route rather than skipping it.",
      },
      {
        q: "My PSA went up after starting TRT — should I worry?",
        a: "A modest rise in the first months is expected. What matters is the size and speed of the change and whether it continues. Your clinician compares it with your baseline and refers to urology when thresholds are met.",
      },
    ],
    related: ["trt-side-effects-and-risks", "haematocrit-and-blood-monitoring-on-trt", "trt-eligibility"],
  },

  {
    slug: "trt-and-heart-health",
    label: "CAUSES & RELATED HEALTH",
    title: "Testosterone and heart health: the TRAVERSE era",
    lede: "For years the cardiovascular safety of TRT was an open question. A large randomised trial has now reported. Here is what it found, what it didn't, and what it means for you.",
    reading: "6 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1534787238916-9ba6764efd4f"),
    imageAlt: "Cyclist riding a road bike through a forest",
    video: explainer("What TRAVERSE changed", "trt-and-heart-health", ["Over 5,000 men with confirmed deficiency and high cardiac risk, randomised to gel or placebo.", "No increase in heart attack, stroke or cardiovascular death.", "More atrial fibrillation, pulmonary embolism and kidney injury in the treated group.", "Reassuring for physiological dosing in confirmed deficiency — not a licence to treat anyone.", "Your cardiovascular risk factors still deserve treatment in their own right."]),
    answer:
      "Low testosterone is associated with higher cardiovascular risk, but association is not cause. The TRAVERSE trial (2023) randomised over 5,000 men with confirmed hypogonadism and high cardiovascular risk to testosterone gel or placebo and found no increase in major adverse cardiovascular events over an average of about three years. It did find more atrial fibrillation, pulmonary embolism and acute kidney injury in the testosterone group. The practical conclusion: TRT appears cardiovascularly safe for appropriately selected and monitored men, and selection and monitoring are not optional.",
    body: [
      {
        h2: "The association that started the debate",
        paras: [
          "Men with low testosterone have higher rates of obesity, type 2 diabetes, metabolic syndrome and cardiovascular disease. Whether low testosterone contributes to those conditions, results from them, or simply travels alongside them is genuinely difficult to untangle, and all three are probably partly true. That is why observational data alone could never settle the safety question.",
        ],
      },
      {
        h2: "What the regulators asked for",
        paras: [
          "After conflicting studies in the early 2010s — some suggesting harm, others benefit — the US FDA required a dedicated cardiovascular safety trial, and UK and European product information carried cautions. The result was TRAVERSE: a placebo-controlled trial in men aged 45 to 80 with symptoms, two low morning testosterone readings, and either existing cardiovascular disease or high risk of it.",
        ],
      },
      {
        h2: "What TRAVERSE found",
        bullets: [
          "No increase in the primary outcome — a composite of cardiovascular death, heart attack and stroke — versus placebo.",
          "Higher rates of atrial fibrillation, pulmonary embolism and acute kidney injury with testosterone.",
          "More fractures in the testosterone group, a finding that surprised investigators and is being studied further.",
          "Modest improvements in sexual function and mood; no meaningful improvement in overall energy on the measures used.",
        ],
      },
      {
        h2: "What it does not tell us",
        paras: [
          "TRAVERSE used a gel and aimed for mid-normal levels in older men with real deficiency. It says little about high-dose injections, about men with normal testosterone taking it for performance, or about treatment lasting many decades. The reassurance applies to treatment that resembles the trial: confirmed deficiency, physiological dosing, monitored.",
        ],
      },
      {
        h2: "How this shapes care at Androgen Health",
        paras: [
          "We confirm deficiency with two morning tests, assess cardiovascular risk at baseline, treat towards physiological rather than supraphysiological levels, watch haematocrit closely because of the clotting signal, and ask about palpitations and calf pain at every review. For men with recent heart attack or stroke, treatment waits. For men with uncontrolled risk factors, those are addressed in parallel — because lowering your cardiovascular risk may do more for you than any hormone.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does TRT lower cardiovascular risk?",
        a: "TRAVERSE did not show a protective effect. TRT should not be started to prevent heart disease; the proven tools for that remain blood pressure, lipids, weight, activity, smoking and diabetes control.",
      },
      {
        q: "I have atrial fibrillation — can I take TRT?",
        a: "Not necessarily excluded, but the increased atrial fibrillation seen in TRAVERSE means this needs a careful, documented discussion and often input from your cardiologist.",
      },
    ],
    related: ["trt-side-effects-and-risks", "haematocrit-and-blood-monitoring-on-trt", "lifestyle-and-testosterone"],
  },

  {
    slug: "how-long-does-trt-take-to-work",
    label: "TREATMENT EXPECTATIONS",
    title: "How long does TRT take to work?",
    lede: "Some changes arrive within weeks; others take the best part of a year. A realistic timeline — from the published evidence, not marketing — helps you judge whether treatment is actually working.",
    reading: "5 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1758922769578-68c5ba000d87"),
    imageAlt: "Man running on a track at sunrise",
    featured: true,
    answer:
      "Libido and sexual interest typically change first, within three to six weeks. Mood, energy and sleep often shift over six to twelve weeks. Changes in body composition — more muscle, less fat — accumulate over three to twelve months with training. Erectile function improves more slowly and may need other treatment too. Bone density improves over years. If nothing has changed by six months at a confirmed therapeutic level, the diagnosis itself is reviewed rather than the dose simply increased.",
    body: [
      {
        h2: "A realistic timeline",
        table: [
          ["", "Typical onset", "Plateau"],
          ["Libido & sexual interest", "3–6 weeks", "~6 months"],
          ["Mood & wellbeing", "3–6 weeks", "3–6 months"],
          ["Energy", "4–12 weeks", "6 months"],
          ["Erectile function", "6 weeks–6 months", "6–12 months"],
          ["Muscle & strength", "12–16 weeks", "6–12 months"],
          ["Fat mass", "12–16 weeks", "12+ months"],
          ["Bone density", "6 months", "2–3 years"],
          ["Glycaemic control", "3–12 months", "12+ months"],
        ],
      },
      {
        h2: "Why your route affects the timeline",
        paras: [
          "Gels reach steady levels within a few days but absorption varies between men, so the first blood test after starting is partly a check that enough is getting in. Short-acting injections produce a peak within a day or two and a trough before the next dose. Long-acting depot injections take two or three doses to reach steady state, which can mean a slower start — and it is why early reviews are timed to the formulation, not a fixed calendar.",
        ],
      },
      {
        h2: "What 'working' means — and what it doesn't",
        paras: [
          "Treatment is working when symptoms that were attributable to low testosterone improve at a confirmed physiological level. It is not working if the only change is a higher number on a blood test. And it is not a failure of treatment if some symptoms persist — fatigue, low mood and poor sleep have other causes that TRT was never going to fix. A good review separates those threads honestly.",
        ],
      },
      {
        h2: "When to reassess rather than escalate",
        bullets: [
          "No meaningful benefit by 6 months at a confirmed mid-normal level — review the diagnosis, not just the dose.",
          "Benefit early that fades — check adherence, absorption, interval and other health changes.",
          "Side effects without benefit — stopping is a legitimate and sometimes right decision.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will I feel a difference in the first week?",
        a: "Some men report an early lift in mood or interest within one to two weeks, particularly with injections; for most, noticeable change takes a few weeks. Early euphoria can also be placebo, which is why we judge response at structured reviews.",
      },
      {
        q: "Can I speed it up with a higher dose?",
        a: "No. Pushing levels above the physiological range increases side effects — especially raised haematocrit — without accelerating the real benefits. Treatment targets the normal range for a reason.",
      },
    ],
    related: ["testosterone-gel-vs-injections", "trt-monitoring", "stopping-trt"],
  },

  {
    slug: "stopping-trt",
    label: "TREATMENT EXPECTATIONS",
    title: "Stopping TRT: what happens when you come off testosterone",
    lede: "Treatment is a decision you can revisit. Here is what to expect if you stop — temporarily or for good — and how to do it safely.",
    reading: "5 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1729280924877-4750c0dba51e"),
    imageAlt: "Man running through woodland",
    answer:
      "When you stop TRT, blood testosterone falls as the medicine clears — days for gels, weeks to months for long-acting injections — while your own production, suppressed during treatment, takes time to restart. Most men experience a return of their original symptoms, sometimes more sharply, over several weeks, before their natural level recovers to roughly where it was before treatment. Recovery can take a few months to a year or more, and is less complete after long treatment or in older men. Stopping should be planned with your clinician, with a follow-up blood test to confirm where you have landed.",
    body: [
      {
        h2: "Why you might stop",
        bullets: [
          "Wanting to conceive — the most common and most legitimate reason.",
          "Side effects, particularly a persistently raised haematocrit.",
          "Lack of clear benefit after a fair trial at a therapeutic level.",
          "A new diagnosis, such as prostate cancer, or a clinician's recommendation.",
          "Cost, logistics or simply wanting to know whether you still need it.",
        ],
      },
      {
        h2: "What happens in your body",
        paras: [
          "During treatment, your pituitary stops producing LH and FSH because it senses plenty of testosterone. When the external supply stops, there is a gap: the medicine clears before your own axis wakes up. Testosterone may fall below where it was before you started, which is why the first weeks can feel worse than the original problem. LH and FSH then rise, the testes respond, and natural production climbs back towards your pre-treatment baseline — but not above it. TRT does not cure the underlying deficiency.",
        ],
      },
      {
        h2: "A realistic recovery timeline",
        paras: [
          "Short-acting preparations clear within days; a long-acting depot can take three months or more. Your own production typically recovers over three to six months, longer after years of treatment or at older ages, and not always completely. Sperm production takes longer still — months to two years. A blood test around three months after stopping, and again at six, tells you where you are.",
        ],
      },
      {
        h2: "Do you need to taper or 'restart'?",
        paras: [
          "There is no evidence that tapering a physiological TRT dose changes the recovery of your own production, and most men stop without tapering. Some clinicians use medicines such as hCG or clomifene to kick-start the axis, particularly when fertility is the goal — these are specialist, off-label treatments and need a supervised plan. Online 'post-cycle therapy' protocols borrowed from steroid use are not evidence-based medicine.",
        ],
      },
      {
        h2: "Stopping safely",
        bullets: [
          "Tell your clinician before stopping, so monitoring and prescriptions are adjusted.",
          "Agree how you will judge success — symptoms, bloods, or both — and when.",
          "Expect a dip and plan around it; do not make big decisions in the trough.",
          "If you restart, it is a fresh clinical decision with fresh baseline bloods.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will I be dependent on TRT for life?",
        a: "Not dependent in the addictive sense, but if your deficiency was genuine and its cause unchanged, symptoms will return when you stop. Where the cause was reversible — weight, sleep, medication — treatment can sometimes be stopped for good.",
      },
      {
        q: "Can I just stop my injections?",
        a: "Physically, yes. Clinically, a planned stop with a follow-up blood test is safer and tells you something useful. Let your clinician know.",
      },
    ],
    related: ["trt-and-fertility", "how-long-does-trt-take-to-work", "trt-side-effects-and-risks"],
  },

  {
    slug: "nhs-vs-private-trt",
    label: "NAVIGATING CARE",
    title: "NHS vs private TRT in the UK: how to decide",
    lede: "Both routes can deliver safe testosterone care. They differ in access, speed, cost and what you are expected to manage yourself. A fair comparison, including when the NHS is the better choice.",
    reading: "6 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1701858986056-c740b7309e89"),
    imageAlt: "Two men sitting together outdoors in conversation",
    answer:
      "The NHS can diagnose and treat testosterone deficiency, free at the point of use, usually through your GP with referral to endocrinology where needed. Access and thresholds vary by area, waits can be long, and borderline results are often not treated. Private online clinics offer faster assessment, broader testing and more frequent review, for a monthly fee plus one-off assessment costs. The best choice depends on how clear-cut your case is, how urgently you need answers, your budget, and whether you want the two routes to work together — which, with shared records, they can.",
    body: [
      {
        h2: "The NHS route, honestly",
        paras: [
          "Start with your GP. If symptoms suggest deficiency, they can arrange a morning testosterone test and, where it is low, repeat it with LH, FSH, prolactin and SHBG. Clear deficiency with a defined cause is usually treated — sometimes in primary care, often after an endocrinology opinion. The strengths are obvious: no cost, integrated records, and specialists who see the rare and complex causes. The friction points are real too: short appointments, variable familiarity with the condition, long waits for endocrinology, and a tendency not to treat borderline or 'age-related' results.",
        ],
      },
      {
        h2: "The private route, honestly",
        paras: [
          "Private clinics — online or in person — offer quicker access, comprehensive panels, and clinicians who focus on this one condition. The costs are explicit: an assessment fee, then a monthly plan covering medication, monitoring and reviews. The risks are also explicit. Some providers over-diagnose, treat men with normal levels, use doses above the physiological range, or skimp on monitoring. Choosing a provider that confirms the diagnosis properly, refuses treatment when it is not indicated and shares information with your GP is the whole game.",
        ],
      },
      {
        h2: "Side by side",
        table: [
          ["", "NHS", "Private (Androgen Health)"],
          ["Cost to you", "Free at point of use", "Assessment £35–£149; plans from £99/month (proposed)"],
          ["Time to first assessment", "Weeks to months", "Days"],
          ["Blood testing", "Core hormones; repeats as needed", "Comprehensive panel incl. free T, SHBG, safety markers"],
          ["Borderline results", "Often not treated", "Assessed individually; may still not be treated"],
          ["Monitoring", "Guideline-based; frequency varies", "Scheduled, included in plan"],
          ["Complex causes", "Endocrinology referral", "Referred back to NHS where appropriate"],
        ],
      },
      {
        h2: "When the NHS is the right answer",
        bullets: [
          "Very low testosterone with symptoms — a clear case the NHS will usually treat.",
          "Suspected pituitary disease, very high prolactin or other red flags needing specialist investigation.",
          "Significant comorbidity where integrated hospital care matters more than speed.",
          "Cost would cause hardship — do not go private because you were told the NHS 'won't help'.",
        ],
      },
      {
        h2: "Making the two work together",
        paras: [
          "Good private care copies your GP in, shares results, and hands back when something falls outside its scope. Some patients are diagnosed privately and later have treatment taken over by the NHS; others use the NHS for diagnosis and private care for faster, more frequent monitoring. Neither is a betrayal of the other. Ask any clinic how it communicates with your GP — and be wary of one that doesn't.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can my GP take over a private TRT prescription?",
        a: "Sometimes, through a shared-care arrangement, if the diagnosis meets local criteria and your GP agrees. It is at the GP's discretion and varies by area. We provide the documentation that makes it possible.",
      },
      {
        q: "Will going private affect my NHS care?",
        a: "No. You remain entitled to NHS care. Keeping your GP informed ensures your records are complete and interactions are picked up.",
      },
    ],
    related: ["trt-cost", "switch-trt-clinic", "trt-eligibility"],
  },

  {
    slug: "shbg-explained",
    label: "TESTING EXPLAINED",
    title: "SHBG explained: why your free testosterone matters",
    lede: "Two men, the same total testosterone, very different symptoms. The explanation is often a protein called sex hormone-binding globulin. Here is what it does and how it changes interpretation.",
    reading: "5 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1641337221253-fdc7237f6b61"),
    imageAlt: "Man holding a dumbbell in a gym",
    answer:
      "Sex hormone-binding globulin (SHBG) is a liver-made protein that binds most of the testosterone in your blood, leaving only about 2–3% free and biologically active. High SHBG — common with ageing, low weight, liver disease, thyroid overactivity and some medicines — means a 'normal' total testosterone can hide a low free level. Low SHBG — common with obesity, insulin resistance and type 2 diabetes — can make a low total look worse than the free level really is. That is why SHBG and calculated free testosterone are part of a proper diagnostic panel when the total is borderline.",
    body: [
      {
        h2: "What SHBG does",
        paras: [
          "Testosterone travels in the blood in three forms: tightly bound to SHBG (the majority), loosely bound to albumin, and free. Only the free fraction — and, to a degree, the albumin-bound portion that can release quickly — is available to tissues. Together, free and albumin-bound testosterone are called 'bioavailable' testosterone. SHBG therefore acts as a reservoir and a regulator, and its level dramatically affects how much testosterone actually reaches your cells.",
        ],
      },
      {
        h2: "What raises and lowers SHBG",
        table: [
          ["", "Raises SHBG", "Lowers SHBG"],
          ["Body composition", "Low body weight", "Obesity, insulin resistance"],
          ["Age", "Increases with age", "—"],
          ["Metabolic", "—", "Type 2 diabetes, fatty liver"],
          ["Thyroid", "Overactive thyroid", "Underactive thyroid"],
          ["Medicines", "Anticonvulsants, oestrogens", "Glucocorticoids, androgens, progestins"],
          ["Liver", "Cirrhosis", "—"],
        ],
      },
      {
        h2: "Why a 'normal' total can still be a problem",
        paras: [
          "A man in his late fifties with a total testosterone of 13 nmol/L sits comfortably in the reference range. If his SHBG is 70 nmol/L, his calculated free testosterone may fall below the range that correlates with symptoms. Conversely, a man with obesity and a total of 8 nmol/L but an SHBG of 15 may have a free level that is entirely adequate — and whose best treatment is weight loss, not testosterone. Reading the total alone misses both.",
        ],
      },
      {
        h2: "How free testosterone is measured",
        paras: [
          "Direct measurement of free testosterone is technically difficult, and the cheap immunoassays sold as 'free T' are unreliable. UK practice is to calculate free testosterone from total testosterone, SHBG and albumin using a validated formula (Vermeulen is the most used). The result is reported in pmol/L; most laboratories regard values below roughly 225 pmol/L as supportive of deficiency when symptoms are present, though thresholds vary and are interpreted in context.",
        ],
      },
      {
        h2: "What this means for diagnosis and treatment",
        paras: [
          "SHBG is why guidelines say that a borderline total testosterone should prompt measurement of SHBG and calculation of free testosterone before deciding. It also informs treatment: men with high SHBG may need different dosing or formulation to achieve adequate free levels, and men with low SHBG due to metabolic disease may respond best to tackling the metabolic problem first. A single number was never going to be enough.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I lower my SHBG naturally?",
        a: "SHBG falls with weight gain and insulin resistance, which is not a route anyone should pursue. Treating an overactive thyroid or reviewing medicines can lower a raised SHBG; otherwise, the aim is to interpret it correctly rather than manipulate it.",
      },
      {
        q: "Does Androgen Health test SHBG?",
        a: "Yes. The comprehensive diagnostic panel includes total testosterone, SHBG and albumin with calculated free testosterone, alongside LH, FSH, prolactin and safety markers.",
      },
    ],
    related: ["normal-testosterone-levels", "causes-of-low-testosterone", "testosterone-testing"],
  },

  {
    slug: "lifestyle-and-testosterone",
    label: "LIFESTYLE",
    title: "Lifestyle and testosterone: what actually moves the needle",
    lede: "Sleep, body fat, alcohol, strength training and stress all influence testosterone. Some effects are large, some are small, and some popular claims are simply wrong. Here is the evidence, ranked honestly.",
    reading: "7 min read",
    updated: "July 2026",
    author: "Androgen Health clinical team",
    image: img("photo-1556911220-dabc1f02913a"),
    imageAlt: "Man preparing fresh vegetables in a kitchen",
    featured: true,
    video: explainer("Lifestyle changes with real evidence", "lifestyle-and-testosterone", ["Body fat is the biggest modifiable driver — losing about 10% raises testosterone meaningfully.", "Short sleep lowers morning testosterone within a week.", "Strength training helps through more muscle and better insulin sensitivity.", "Opioids, heavy alcohol and anabolic steroids suppress natural production.", "\"Testosterone boosters\" have weak or no evidence in healthy men."]),
    answer:
      "The lifestyle factors with the strongest evidence for raising testosterone are losing excess body fat (the single biggest modifiable driver), sleeping enough (short sleep lowers morning testosterone measurably within a week), and keeping alcohol moderate. Regular resistance and aerobic exercise help mainly by improving body composition and insulin sensitivity. Supplements marketed as 'testosterone boosters' have little or no effect in men who are not deficient in a specific nutrient. For some men these changes resolve symptoms; for others with genuine deficiency they are necessary but not sufficient.",
    body: [
      {
        h2: "Body fat: the biggest lever",
        paras: [
          "Excess fat tissue converts testosterone to oestrogen via aromatase, lowers SHBG, and dampens the pituitary signal. Men with obesity frequently have low total testosterone, and meaningful weight loss — around 10% of body weight or more — raises it, sometimes back into the normal range. Bariatric surgery studies show some of the largest rises seen with any intervention. If weight is part of your picture, it is not a side issue; it may be the issue.",
        ],
      },
      {
        h2: "Sleep: cheap, fast, underrated",
        paras: [
          "Testosterone is produced largely during sleep, and restricting healthy young men to about five hours a night lowers their daytime testosterone within a week. Obstructive sleep apnoea — common in men with low testosterone, and worsened by TRT if untreated — suppresses levels further. Snoring, unrefreshing sleep and daytime sleepiness should be assessed before anyone reaches for a prescription.",
        ],
      },
      {
        h2: "Exercise: effective, with caveats",
        bullets: [
          "Resistance training produces a short-lived rise in testosterone after a session; its lasting benefit comes from more muscle, less fat and better insulin sensitivity.",
          "Aerobic exercise helps mainly through weight and metabolic health.",
          "Very high training volumes with inadequate recovery and fuelling can lower testosterone — the 'overtrained' endurance athlete pattern.",
        ],
      },
      {
        h2: "Alcohol, smoking and other substances",
        paras: [
          "Heavy drinking suppresses testosterone directly and through liver damage; moderate drinking has a smaller, inconsistent effect. Cannabis and opioids — including prescribed opioid painkillers — lower testosterone, with opioids being a common and under-recognised cause in men with chronic pain. Anabolic steroid use shuts down natural production and can take a very long time to recover from.",
        ],
      },
      {
        h2: "Diet and supplements: separating evidence from marketing",
        paras: [
          "Correcting a genuine vitamin D or zinc deficiency can modestly raise testosterone; taking either when you are replete does little. Very low-fat or severely calorie-restricted diets can lower testosterone. 'Testosterone boosters' — tribulus, fenugreek, D-aspartic acid, ashwagandha and the rest — have weak, inconsistent or absent evidence in healthy men, and some products have been found to contain undeclared pharmaceutical ingredients. Spend the money on sleep, food and a gym.",
        ],
      },
      {
        h2: "Stress, mood and the honest limit of lifestyle",
        paras: [
          "Chronic stress and depression lower testosterone and also produce most of the symptoms attributed to it, which is why treating mental health is part of good assessment. But lifestyle has limits: a man with primary testicular failure or pituitary disease will not fix his levels by sleeping more. The clinical skill is telling the two situations apart — and being willing to say 'try this first, then we retest' as readily as 'this needs treatment'.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much weight do I need to lose to raise my testosterone?",
        a: "Studies generally show meaningful increases with around 10% or more of body weight lost, with larger losses producing larger rises. Even smaller losses improve insulin sensitivity and energy.",
      },
      {
        q: "Should I try lifestyle changes before TRT?",
        a: "Where weight, sleep, alcohol or medication are plausible contributors, yes — with a retest after a defined period. Where deficiency is severe or has a clear organic cause, treatment and lifestyle changes may proceed together.",
      },
    ],
    related: ["causes-of-low-testosterone", "trt-and-heart-health", "low-testosterone-symptoms"],
  },
];
